import { useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Alert, Button, Card, Chip, Spinner, Tag, TextField } from "@/components/ui";
import { errorMessage } from "@/lib/api/error";
import { useGenerateIcebreaker } from "@/lib/api/queries";
import type { IcebreakerGroupSize, IcebreakerRelation, SpeechOccasion } from "@/lib/api/types";
import { copyText } from "@/lib/clipboard";

// 세 답 모두 닫힌 목록이다 — 자유 입력으로 받으면 서버 프롬프트가 예측 불가능해진다.
// 자리는 건배사와 **같은 목록**이고, 서버도 한 벌만 들고 있다(`services/toast.py`).
const OCCASIONS: SpeechOccasion[] = ["그냥 회식", "송년회", "승진·발령", "환영·환송", "MT·워크숍"];
const RELATIONS: IcebreakerRelation[] = [
  "같은 팀",
  "다른 팀과 함께",
  "타사·외부 손님",
  "처음 보는 사이",
];
const GROUP_SIZES: IcebreakerGroupSize[] = ["4명 이하", "5~10명", "10명 이상"];

/** 복사하는 건 자리에서 읽을 말이다 — 주제 이름은 화면에서 고를 때만 쓰고 넣지 않는다.
 *  이어 물을 말은 있으면 다음 줄에 붙인다. */
function copyLines(question: string, followup: string): string {
  return followup ? `${question}\n${followup}` : question;
}

export default function IcebreakerPage() {
  const [occasion, setOccasion] = useState<SpeechOccasion>("그냥 회식");
  const [relation, setRelation] = useState<IcebreakerRelation>("같은 팀");
  const [groupSize, setGroupSize] = useState<IcebreakerGroupSize>("5~10명");
  const [avoid, setAvoid] = useState("");

  const generate = useGenerateIcebreaker();

  // 어떤 답으로 만든 결과인지는 mutation 이 들고 있다. 지금 폼 상태로 결과를
  // 설명하면, 만든 뒤에 답만 바꿔 놓은 화면이 거짓말을 한다(건배사와 같은 이유).
  const asked = generate.variables;
  const topics = generate.data?.topics ?? [];

  function handleGenerate() {
    generate.mutate(
      { occasion, relation, group_size: groupSize, avoid },
      {
        onError: (error) =>
          toast.error(errorMessage(error, "만들지 못했어요. 다시 시도해 주세요.")),
      }
    );
  }

  async function handleCopy(question: string, followup: string) {
    const ok = await copyText(copyLines(question, followup));
    if (ok) toast.success("질문을 복사했어요");
    else toast.error("복사하지 못했어요");
  }

  return (
    <AppShell title="대화 주제">
      <p className="text-body-2 text-label-alternative">
        정적이 흐를 때 꺼낼 질문을 만들어 드려요. 세 가지만 알려 주세요.
      </p>

      <Card variant="filled" padding={16} className="mt-4">
        <span className="text-label-1 font-semibold text-label-normal">무슨 자리예요?</span>
        <div className="mt-2 flex flex-wrap gap-2">
          {OCCASIONS.map((value) => (
            <Chip key={value} active={occasion === value} onClick={() => setOccasion(value)}>
              {value}
            </Chip>
          ))}
        </div>

        <div className="mt-4">
          <span className="text-label-1 font-semibold text-label-normal">누가 앉아 있어요?</span>
          {/* 같은 팀과 타사 손님은 꺼낼 수 있는 말이 전혀 다르다. */}
          <div className="mt-2 flex flex-wrap gap-2">
            {RELATIONS.map((value) => (
              <Chip key={value} active={relation === value} onClick={() => setRelation(value)}>
                {value}
              </Chip>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <span className="text-label-1 font-semibold text-label-normal">몇 명이에요?</span>
          {/* 인원이 많으면 모두가 차례로 답하는 질문이 자리를 20분 잡아먹는다. */}
          <div className="mt-2 flex flex-wrap gap-2">
            {GROUP_SIZES.map((value) => (
              <Chip key={value} active={groupSize === value} onClick={() => setGroupSize(value)}>
                {value}
              </Chip>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <TextField
            label="피하고 싶은 이야기 있어요?"
            placeholder="예) 야구, 주식"
            value={avoid}
            onChange={(e) => setAvoid(e.target.value)}
            maxLength={60}
            helperText="나이·연봉·결혼 같은 사적인 주제는 적지 않아도 빼 드려요."
          />
        </div>
      </Card>

      {generate.isError && (
        <div className="mt-4">
          {/* 토스트로만 알리면 사용자가 만들기를 다섯 번 누른다. 상태를 새로 만들지
              않고 mutation 의 에러를 그대로 그린다 — 다음 mutate 에서 사라진다. */}
          <Alert tone="cautionary">
            {errorMessage(generate.error, "만들지 못했어요. 다시 시도해 주세요.")}
          </Alert>
        </div>
      )}

      {generate.isPending ? (
        <div className="mt-4 flex items-center gap-2">
          <Spinner size={20} />
          <span className="text-body-2 text-label-alternative">주제를 고르고 있어요…</span>
        </div>
      ) : (
        <div className="mt-4">
          <Button fullWidth onClick={handleGenerate}>
            {topics.length > 0 ? "다시 만들기" : "만들어 주세요"}
          </Button>
        </div>
      )}

      {topics.length > 0 ? (
        <div className="mt-8">
          <h2 className="text-label-1 font-semibold text-label-normal">이런 이야기가 어때요</h2>
          {asked && (
            <p className="mt-1 text-caption-1 text-label-alternative">
              {`${asked.occasion} · ${asked.relation} · ${asked.group_size}`}
            </p>
          )}
          <div className="mt-3 flex flex-col gap-3">
            {/* 같은 주제 이름이 겹칠 수 있어 key 에 순번을 함께 넣는다. */}
            {topics.map((item, index) => (
              <Card
                key={`${item.topic}-${index}`}
                padding={16}
                className="animate-pop-in motion-reduce:animate-none"
                style={{ animationDelay: `${index * 70}ms` }}
              >
                <div className="flex items-center justify-between gap-2">
                  <Tag size="small">{item.topic}</Tag>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => handleCopy(item.question, item.followup)}
                  >
                    복사
                  </Button>
                </div>
                <p className="mt-2 text-body-2 text-label-normal">{item.question}</p>
                {item.followup && (
                  // 대답이 한마디로 끊겼을 때 이어 붙일 말. 질문과 같은 칸에 두면
                  // 자리에서 둘을 한 번에 읽어 버린다.
                  <p className="mt-2 text-caption-1 text-label-alternative">
                    <span className="font-semibold text-label-normal">이어서</span>{" "}
                    {item.followup}
                  </p>
                )}
              </Card>
            ))}
          </div>
        </div>
      ) : (
        !generate.isPending && (
          <p className="mt-3 text-caption-1 text-label-alternative">
            자리에서 그대로 읽을 질문 다섯 개를 만들어 드려요.
          </p>
        )
      )}
    </AppShell>
  );
}
